import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, ShieldOff } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { UserSelect } from '@/components/UserSelect'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiErrorDescription } from '@/services/core/apiError'
import { TwoFactorRecoveryApi } from '@/services/users/twoFactorRecoveryApi'

interface TwoFactorRecoveryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
}

/**
 * Starts cancelling a user's 2FA sign-in requirement. Another user with 2FA
 * has to confirm it from an email before it takes effect.
 */
export function TwoFactorRecoveryDialog({
  open,
  onOpenChange,
  userId,
}: TwoFactorRecoveryDialogProps) {
  const { t } = useTranslation()
  const [verifierId, setVerifierId] = useState('')

  const { data: verifiers = [], isLoading: verifiersLoading } = useQuery({
    queryKey: ['2fa-verifiers', userId],
    queryFn: () => TwoFactorRecoveryApi.getVerifiers(userId),
    enabled: open,
  })
  const { data: verifying = [], isLoading: verifyingLoading } = useQuery({
    queryKey: ['2fa-verifying', userId],
    queryFn: () => TwoFactorRecoveryApi.getVerifying(userId),
    enabled: open,
  })
  const loading = verifiersLoading || verifyingLoading

  const mutation = useMutation({
    mutationFn: () => TwoFactorRecoveryApi.initiate(userId, verifierId),
    onSuccess: () => {
      toast.success(t('twoFactorRecovery.started'), { duration: 3000 })
      setVerifierId('')
      onOpenChange(false)
    },
    onError: (error) => {
      toast.error(t('twoFactorRecovery.failed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('twoFactorRecovery.title')}</DialogTitle>
          <DialogDescription>{t('twoFactorRecovery.description')}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex flex-col gap-4">
            {verifiers.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <FieldLabel tooltip={t('twoFactorRecovery.verifierHelp')}>
                  {t('twoFactorRecovery.verifier')}
                </FieldLabel>
                <UserSelect
                  value={verifierId}
                  onChange={setVerifierId}
                  users={verifiers.map((u) => ({
                    id: u.id,
                    displayName: u.displayName ? `${u.displayName} (${u.username})` : u.username,
                  }))}
                  placeholder={t('twoFactorRecovery.selectVerifier')}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {verifying.length
                  ? t('twoFactorRecovery.noMoreVerifiers')
                  : t('twoFactorRecovery.noVerifiers')}
              </p>
            )}
            {verifying.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('twoFactorRecovery.existingVerifiers')}</FieldLabel>
                <ul className="flex flex-col gap-0.5 text-sm">
                  {verifying.map((u) => (
                    <li key={u.id}>{u.username}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          {verifiers.length > 0 && (
            <Button
              type="button"
              loading={mutation.isPending}
              disabled={!verifierId}
              onClick={() => mutation.mutate()}
            >
              <ShieldOff className="size-4" />
              {t('twoFactorRecovery.start')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
